// Per-tab marker for the super-admin "Remote Login" handshake.
//
// The super-admin backend redirects an opener / window.open() to
// `/product-selection?access_token=<jwt>` with a JWT that carries
// `loginMode: 'remote'`. This app has no separate session store —
// it writes the bearer into the same `accessToken` localStorage slot
// the normal login flow uses (so axios interceptors and route guards
// work without changes). What it DOESN'T want to do is leak that
// fact to other tabs or to a previously-logged-in normal user, so
// we keep a per-tab flag here in sessionStorage.
//
//  - isActive()     → true after captureFromUrl() succeeds
//  - setActive()    → mark this tab as remote-login
//  - clear()        → drop the flag (called on /logout or when the
//                     user clicks "Return to Admin")
//
// sessionStorage is intentionally per-tab — see step (4) of MDG-215
// for why we don't share the flag with sibling tabs.

import { tokenStorage } from './tokenStorage'

const REMOTE_LOGIN_KEY = 'mdg.remoteLoginActive'

function safeGet(key) {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}
function safeSet(key, value) {
  try {
    sessionStorage.setItem(key, value)
  } catch {
    // SSR / private-mode-disabled — fall through; flag is advisory.
  }
}
function safeRemove(key) {
  try {
    sessionStorage.removeItem(key)
  } catch {
    // ignore
  }
}

export const remoteLogin = {
  isActive: () => safeGet(REMOTE_LOGIN_KEY) === '1',
  setActive: () => safeSet(REMOTE_LOGIN_KEY, '1'),
  clear: () => safeRemove(REMOTE_LOGIN_KEY),
}

// Capture a `?access_token=<jwt>` from the current URL. Runs synchronously
// so it can be invoked at module top-level (before React mounts the route
// tree) — otherwise `AuthProtected` would bounce the user to /login on
// the first render because the bearer isn't in localStorage yet.
//
// Returns `true` when a token was captured, `false` otherwise. Idempotent:
// after the URL is scrubbed, subsequent loads won't see the param.
export function captureFromUrl() {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(window.location.search)
  const token = params.get('access_token')
  if (!token) return false

  // Same write path as POST /login → Login.jsx → tokenStorage.setAccessToken.
  tokenStorage.setAccessToken(token)
  remoteLogin.setActive()

  // Scrub the URL so the JWT never sits in browser history, copy-paste, or
  // the Referer header on subsequent in-app navigations.
  const clean = window.location.pathname + window.location.hash
  window.history.replaceState({}, '', clean)

  // Single audit hook — keep this the only place we ever log the capture so
  // production landings are easy to grep.
  // eslint-disable-next-line no-console
  console.info('[auth] remote-login bearer captured')
  return true
}
